import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'
import './styles/control-centre.css'
import { updateService } from './services/updateService'
import { initializeErrorHandlers } from './services/errorLogService'

// Silence console noise in production (dev keeps full logging).
if (import.meta.env.PROD || import.meta.env.MODE === 'production') {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.error = () => {};
}

// Custom Service Worker Registration for Push Notifications

// Remove initial loader function - wait for React to actually render visible content
// This ensures no blank screen between loader and React content
function removeInitialLoader() {
  let attempts = 0;
  const maxAttempts = 40; // Check for up to 2 seconds (40 * 50ms) - covers 3-4 second startup
  
  const checkAndRemove = () => {
    attempts++;
    
    // Check if React has rendered something visible (not just empty divs)
    const root = document.getElementById('root');
    if (!root) {
      if (attempts < maxAttempts) {
        setTimeout(checkAndRemove, 50);
      }
      return;
    }
    
    // Check for actual visible content (not just empty containers)
    const hasVisibleContent = root.querySelector('div[class*="LoadingScreen"]') !== null ||
                              root.querySelector('div[class*="login"]') !== null ||
                              root.querySelector('header') !== null ||
                              root.querySelector('main') !== null ||
                              (root.children.length > 0 && root.textContent && root.textContent.trim().length > 0);
    
    if (hasVisibleContent) {
      // React has rendered visible content - mark as mounted and hide loader
      document.body.classList.add('react-mounted');
      
      const loader = document.getElementById('initial-loader');
      if (loader) {
        // Fade out smoothly to prevent flash
        loader.style.transition = 'opacity 0.3s ease-out';
        loader.style.opacity = '0';
        setTimeout(() => {
          loader.style.display = 'none';
          loader.remove();
        }, 300);
      }
    } else if (attempts < maxAttempts) {
      // React hasn't rendered visible content yet - check again
      setTimeout(checkAndRemove, 50);
    } else {
      // Timeout - hide loader anyway to prevent infinite loading
      console.warn('Loader removal timeout - React may not have rendered visible content');
  document.body.classList.add('react-mounted');
  const loader = document.getElementById('initial-loader');
  if (loader) {
        loader.style.transition = 'opacity 0.3s ease-out';
        loader.style.opacity = '0';
        setTimeout(() => {
    loader.style.display = 'none';
    loader.remove();
        }, 300);
      }
    }
  };
  
  // Start checking after React has had time to start rendering
  // Use multiple requestAnimationFrame to ensure React has started
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        checkAndRemove();
      });
    });
  });
}

// Register service worker for caching and push notifications
// Update detection is handled by UpdateService via version.json checking
async function registerCustomServiceWorker() {
  // Skip in dev: the SW's app-shell caching intercepts Vite's module requests
  // and intermittently serves a blank page. Also unregister any stale one.
  if (import.meta.env.DEV) {
    try {
      const regs = await navigator.serviceWorker?.getRegistrations();
      regs?.forEach((r) => void r.unregister());
    } catch { /* no SW support */ }
    return;
  }
  // Skip in native app (Capacitor) - SW is for web/PWA only; native uses APNs etc.
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) return;
  } catch {
    // @capacitor/core not loaded (e.g. dev without Capacitor)
  }
  if ('serviceWorker' in navigator) {
    try {
      console.log('🔧 Registering service worker...');
      
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Always check for updates
      });
      
      console.log('✅ Service Worker registered successfully:', registration.scope);
      
      // Handle service worker errors
      registration.addEventListener('error', (error) => {
        console.error('❌ Service Worker error:', error);
        clearAllCaches();
      });
      
      return registration;
    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
      clearAllCaches();
    }
  } else {
    console.warn('⚠️ Service Workers not supported');
  }
}

// Function to clear all caches when service worker fails
async function clearAllCaches() {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('🗑️ All caches cleared due to service worker issues');
    }
  } catch (error) {
    console.warn('⚠️ Failed to clear caches:', error);
  }
}

// Initialize error handlers FIRST - catch errors from the start
// This includes service worker error listeners
initializeErrorHandlers();

// Initialize service worker and update service in background (non-blocking)
// Don't wait for these - they can happen after React renders for faster startup
(async () => {
  try {
    // Skip update service on native (Capacitor). It checks version.json and can reload the page
    // when returning from background, which causes a blank screen in the iOS WebView.
    // Native updates are delivered via TestFlight/App Store, not version.json.
    const isNative = await import('@capacitor/core').then(({ Capacitor }) => Capacitor.isNativePlatform()).catch(() => false);
    if (!isNative) {
      updateService.initialize().catch(err => {
        console.warn('UpdateService initialization failed (non-critical):', err);
      });
    }

    // Register service worker in background (web/PWA only; skipped inside for native)
    registerCustomServiceWorker().catch(err => {
      console.warn('Service worker registration failed (non-critical):', err);
    });
  } catch (error) {
    console.warn('Background initialization error (non-critical):', error);
  }
})();

const rootElement = document.getElementById('root');

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
}

// Remove loader AFTER React has rendered (not before)
// Use multiple requestAnimationFrame to ensure React has painted
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    removeInitialLoader();
  });
});
